<script setup lang="ts">
import { CartDiscount, CartDiscountReference } from '@commercetools/platform-sdk';
import { money } from '@nuts/auto-delivery-sdk/dist/utils/format';
import { dollars } from '@nuts/auto-delivery-sdk/dist/utils/money';
import dayjs from 'dayjs';
import { computed, onBeforeUnmount, ref, watch } from 'vue';
import { useStore } from 'vuex';

import BaseBodyText from '@/components/base/typography/BaseBodyText.vue';
import SmallBodyText from '@/components/base/typography/SmallBodyText.vue';
import UnstyledButton from '@/components/base/UnstyledButton.vue';
import { useDiscount } from '@/composables/useDiscount';
import {
  getAmountToActivateDiscount,
  getExpandedCartDiscounts,
  isAbsoluteDiscount,
  isGiftLineItemDiscount,
  isRelativeDiscount,
  isShippingDiscount,
} from '@/lib/cart/discounts';
import { Money } from '@/utils/money';
import { reportError } from '@/utils/reportError';

const props = defineProps<{
  discountCode: {
    code?: string;
    label: string;
    cartDiscounts?: CartDiscountReference[];
  };
  pendingCodes?: boolean;
  subTotalPrice: Money;
}>();

const loading = defineModel<boolean>('loading', { required: true });

const store = useStore();

const { appliedDiscountCodes, applyDiscountCode, removeDiscountCode } = useDiscount(store);

const applied = computed(() =>
  appliedDiscountCodes.value.some(
    (appliedDiscountCode) => appliedDiscountCode.label === props.discountCode.label,
  ),
);
const actionStatus = ref<'Apply' | 'Applying' | 'Applied' | 'Remove' | 'Removed' | 'Removing'>(
  applied.value ? 'Remove' : 'Apply',
);
const isPending = computed(() => !['Apply', 'Remove'].includes(actionStatus.value));

watch(applied, (newValue) => {
  actionStatus.value = newValue ? 'Remove' : 'Apply';
});

type AugmentedCartDiscount = {
  amountRemaining: Money;
  threshold: Money;
  cartDiscount?: CartDiscount;
};

const augmentedCartDiscount = computed(() =>
  getExpandedCartDiscounts(props.discountCode.cartDiscounts)
    .reduce<AugmentedCartDiscount[]>((accum, discount) => {
      const amount = getAmountToActivateDiscount(discount.cartPredicate, props.subTotalPrice);
      if (amount && dollars(amount.amountRemaining) > 0) {
        accum.push({ ...amount, cartDiscount: discount });
      }
      return accum;
    }, [])
    .sort((a, b) => dollars(Money.subtract(a.threshold, b.threshold)))
    .shift(),
);

const amountOffFromMessage = computed(() => {
  const { cartDiscount, threshold } = augmentedCartDiscount.value || {};
  if (cartDiscount) {
    if (isShippingDiscount(cartDiscount)) {
      return 'free shipping';
    }
    if (isAbsoluteDiscount(cartDiscount)) {
      return `$${dollars(cartDiscount.value.money[0])} off your $${dollars(threshold)}+ order`;
    }
    if (isRelativeDiscount(cartDiscount)) {
      return `${cartDiscount.value.permyriad / 100}% off your $${dollars(threshold)}+ order`;
    }
    if (isGiftLineItemDiscount(cartDiscount)) {
      return 'a free gift';
    }
    return 'redeeming the promo';
  }
  return undefined;
});
const validUntilMessage = computed(() => augmentedCartDiscount.value?.cartDiscount?.validUntil);

const applyDiscount = async (discountCode: string) => {
  loading.value = true;
  actionStatus.value = 'Applying';
  try {
    await applyDiscountCode(discountCode);

    actionStatus.value = 'Applied';
    loading.value = false;

    setTimeout(() => {
      actionStatus.value = 'Remove';
    }, 3000);
  } catch (error) {
    actionStatus.value = 'Apply';
    reportError(error);
    loading.value = false;
  }
};

const removeDiscount = async (discountCode: string) => {
  loading.value = true;
  actionStatus.value = 'Removing';
  try {
    await removeDiscountCode(discountCode);

    actionStatus.value = 'Removed';
    loading.value = false;

    setTimeout(() => {
      actionStatus.value = 'Apply';
    }, 3000);
  } catch (error) {
    actionStatus.value = 'Remove';
    reportError(error);
    loading.value = false;
  }
};

function handleClick(code: string) {
  if (applied.value) {
    removeDiscount(code);
  } else {
    applyDiscount(code);
  }
}

onBeforeUnmount(() => {
  loading.value = false;
});
</script>

<template>
  <div>
    <div v-if="pendingCodes" class="flex justify-between gap-2">
      <BaseBodyText>{{ discountCode.label }}</BaseBodyText>
      <BaseBodyText class="text-neutral-500">Pending</BaseBodyText>
    </div>
    <div v-else-if="discountCode.code" class="flex justify-between gap-4">
      <BaseBodyText>{{ discountCode.label }}</BaseBodyText>
      <UnstyledButton
        :class="isPending ? 'text-neutral-500' : 'underline text-nuts-cyan-600'"
        :disabled="isPending"
        @click="handleClick(discountCode.code)"
      >
        <BaseBodyText>{{ actionStatus }}</BaseBodyText>
      </UnstyledButton>
    </div>
    <div v-if="augmentedCartDiscount && amountOffFromMessage" class="relative mt-0.5">
      <div
        class="absolute -top-2.5 right-4 w-0 h-0 border-[7.5px] border-b-[15px] border-t-0 border-solid border-transparent border-b-nuts-orange-50"
      />
      <SmallBodyText class="px-2 py-1.5 rounded-md bg-nuts-orange-50 text-amber-700">
        Your are only {{ money(augmentedCartDiscount.amountRemaining) }} away from getting
        {{ amountOffFromMessage
        }}{{
          validUntilMessage &&
          `. Offer expires on ${dayjs(validUntilMessage).format('MMMM D, YYYY')}`
        }}
      </SmallBodyText>
    </div>
  </div>
</template>
